import { put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Relationship } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { DataSelectors, EngineState } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { Commands } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store/internal/actions";
import { UiStateSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store/internal/selectors/ui-state";
import {
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentServiceFactory, FieldInstanceValue, GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { DataSelector, Identifier, TreeEngineState } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/core/store";
import { TreeEngineSelectors } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/client";

import { isFieldInstanceValue, isGroupInstanceArray } from "../../../utilities/documentHelpers";
import { entityInstancePathFromString } from "../../../utilities/pathHelpers";
import { PATH_TO_NODE_TEXT } from "../../../utilities/variables/documentModelPaths";

import { confirmTextTree, ConfirmTextTreePayload } from "./actions";

const documentService = new DocumentServiceFactory().getDocumentService();

/**
 * Saga um die ausgewählten Textbausteine zu übernehmen und den Baum zu schließen.
 * Wird durch die confirmTextTree Action ausgelöst, die vom CustomTextbausteineTreeEngine dispatcht wird.
 */
export function* watchNodeConfirmSaga(): SagaGenerator<void> {
	yield* takeLatest(confirmTextTree.match, handle);
}

type EventAction = Action<ConfirmTextTreePayload>;
function* handle(action: EventAction): SagaGenerator<void> {
	const { activityId } = action.payload;

	const treeEngineState = yield* select(TreeEngineSelectors.engineState(activityId));
	const dataState = yield* select(TreeEngineSelectors.dataState(activityId));
	const uiState = yield* select(TreeEngineSelectors.uiState(activityId));

	const activity = yield* select(ActivitySelectors.activityById(activityId));
	const initiatingActivityId = activity?.initiatingActivityId;

	const multiSelectionNodes = uiState.multiSelectionNodes;

	if (activity === undefined) {
		throw new Error("Internal Error");
	}

	if (!treeEngineState || !initiatingActivityId || !dataState) {
		return;
	}

	const relationIdentifiers = getRelationIdentifiers(multiSelectionNodes);
	const treeEngineData = dataState.data;

	const documentIdentifiers = getDocumentIdentifiersFromRelationIdentifiers(relationIdentifiers, treeEngineData);

	const texts = getTextsFromDocumentIdentifiers(documentIdentifiers, treeEngineState);

	const formEngineState = yield* select(FormEngineSelectors.engineState(initiatingActivityId));

	const path = activity.descriptor.targetDocumentPath;

	if (!formEngineState || !path) {
		return;
	}

	const screenLocationStack = UiStateSelectors.screenLocationStack()(formEngineState);

	if (texts.length > 0) {
		// fallback for previous data
		const targetFieldName = (activity.descriptor.targetFieldName as string | undefined) ?? "text";
		const newData = createNewData(path, formEngineState, texts, targetFieldName);
		yield* put(ActivityActions.setData({ activityId: initiatingActivityId, data: { document: newData } }));
		yield* put(
			FormEngineActions.command({
				engineEvent: Commands.changeScreenState({ index: screenLocationStack.length - 1, dirty: true }),
				activityId: activity.initiatingActivityId
			})
		);
	}

	yield* put(ActivityActions.cancel({ activityId }));
}

/**
 * Converts relation identifiers to its corresponding text identifiers
 * @param relationIdentifiers
 * @param treeEngineData
 */
export const getDocumentIdentifiersFromRelationIdentifiers = (
	relationIdentifiers: Identifier[],
	treeEngineData: TreeEngineState.Data
): Identifier[] => {
	const entities = relationIdentifiers.flatMap(identifier => {
		const entity = treeEngineData[identifier.type]?.[identifier.id];
		return entity && TreeEngineState.Link.isAssignableFrom(entity)
			? (entity?.linkRef.linkDescriptor as Relationship.LinkDescriptorResponse).entities
			: [];
	});

	/**
	 * Contains the ids and the types for all selected texts.
	 */
	return entities.flatMap(entity =>
		entity.docRef && entity.role ? { id: entity.docRef, type: entity.modelName } : []
	);
};

/**
 * Returns all relation identifiers found in a multi selection
 * multi selections are structured like this: {Relation[1]=>Relation[2]=>Relation[3]: selected}
 * the identifier corresponding to the text is always the last one in the key of the object
 * this function creates an identifier from those information
 * TODO: more checks for wrong formatting etc
 * @param multiSelection
 */
export const getRelationIdentifiers = (multiSelection: TreeEngineState.MultiSelectionNodes): Identifier[] => {
	const selectedElements = Object.entries(multiSelection).flatMap(([key, value]) =>
		value === TreeEngineState.MultiSelectionState.SELECTED ? (key ?? []) : []
	);

	return selectedElements.reduce<Identifier[]>((result, currentValue) => {
		const path = TreeEngineState.NodePath.fromString(currentValue);
		const linkIdentifier = TreeEngineState.NodePath.toLinkIdentifier(path);
		if (linkIdentifier !== undefined) {
			result.push(linkIdentifier);
		}

		return result;
	}, []);
};

/**
 * Gets the node corresponding to an identifier and reads the text from it. Then returns a list of all selected texts.
 * @param documentIdentifiers
 * @param treeEngineState
 */
export const getTextsFromDocumentIdentifiers = (
	documentIdentifiers: Identifier[],
	treeEngineState: TreeEngineState
): FieldInstanceValue[] => {
	return documentIdentifiers
		.flatMap(identifier => {
			const textNode = DataSelector.node(identifier)(treeEngineState);
			return textNode ?? [];
		})
		.flatMap(textNode => {
			const text = documentService.getAssignedObject(textNode.document as GroupInstance, PATH_TO_NODE_TEXT);
			return isFieldInstanceValue(text) ? text : [];
		});
};

/**
 * Creates the new data from the path to the inline repeat and the texts supplied to the function.
 * @param documentPathToRepeatableGroup
 * @param formEngineState
 * @param texts
 * @param fieldName - the field name within the repeatable group to write the text to (defaults to "text")
 */
export const createNewData = (
	documentPathToRepeatableGroup: string,
	formEngineState: EngineState,
	texts: (readonly GroupInstance[] | Readonly<GroupInstance> | FieldInstanceValue | undefined)[],
	fieldName = "text"
): GroupInstance => {
	const entityInstance = entityInstancePathFromString(documentPathToRepeatableGroup);

	const document = DataSelectors.document()(formEngineState) as GroupInstance;

	const repeatData = documentService.getAssignedObject(document, entityInstance);

	const selectedTextbausteinData = texts.map(text => ({ [fieldName]: text }));

	const newData = !isGroupInstanceArray(repeatData)
		? selectedTextbausteinData
		: [...repeatData, ...selectedTextbausteinData];

	return documentService.updateEntityInstance(
		document,
		entityInstance,
		newData,
		formEngineState.models.documentModel
	);
};
